import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import MarketingNav from "@/components/app/MarketingNav";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "checking" : "missing");
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const calledRef = useRef(false);  // guard against React StrictMode double-invocation

  useEffect(() => {
    if (!token) return;
    if (calledRef.current) return;  // de-dupe across React 18 StrictMode double-mount
    calledRef.current = true;
    api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(({ data }) => { setStatus("ok"); setEmail(data.email || ""); })
      .catch((e) => {
        setStatus("error");
        setErrorMsg(formatApiError(e.response?.data?.detail) || "Verification failed");
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-2xl mx-auto px-6 lg:px-10 py-20 text-center" data-testid="verify-email-page">
          {status === "checking" && (
            <div data-testid="verify-checking">
              <Loader2 className="w-12 h-12 mx-auto animate-spin" strokeWidth={1.5} />
              <h1 className="font-semibold tracking-tight mt-6 text-3xl">Verifying…</h1>
            </div>
          )}
          {status === "ok" && (
            <div data-testid="verify-ok">
              <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" strokeWidth={1.5} />
              <h1 className="font-semibold tracking-tight mt-6 text-4xl">Email confirmed.</h1>
              {email && <p className="text-sm text-muted-foreground mt-2 font-mono-data">{email}</p>}
              <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                Receipts, post reminders, and the occasional product update will land here. Now go ship.
              </p>
              <Link to="/app" className="inline-block mt-8">
                <Button className="rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="verify-cta-app">Open workspace →</Button>
              </Link>
            </div>
          )}
          {(status === "error" || status === "missing") && (
            <div data-testid="verify-error">
              <AlertCircle className="w-12 h-12 mx-auto text-amber-700" strokeWidth={1.5} />
              <h1 className="font-semibold tracking-tight mt-6 text-3xl">
                {status === "missing" ? "No verification token." : "Couldn't verify."}
              </h1>
              <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                {status === "missing"
                  ? "This link is incomplete. Open the link in the email we sent you."
                  : (errorMsg || "Maybe the link expired or was already used. Try resending it from your account menu.")}
              </p>
              <Link to="/app" className="inline-block mt-8">
                <Button variant="outline" className="rounded-none h-11" data-testid="verify-back-app">Back to app</Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
